import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { MapContainer, TileLayer, Marker, Popup, LayersControl } from "react-leaflet";
import L from "leaflet";
import { statusClass } from "../lib/helpers";
import { ExternalLink } from "lucide-react";

const STATUS_COLORS = {
  NEW: "#0284C7", IN_PROGRESS: "#F59E0B", WON: "#10B981", LOST: "#64748B", CONTACTED: "#8B5CF6",
};

function makeIcon(num, status) {
  const color = STATUS_COLORS[(status || "NEW").toUpperCase()] || "#0284C7";
  // Grow the pill horizontally when the lead number has more digits so the label always fits.
  const label = String(num);
  const width = Math.max(30, 14 + label.length * 7);
  return L.divIcon({
    className: "",
    html: `<div style="background:${color};color:#fff;border-radius:15px;min-width:30px;width:${width}px;height:30px;padding:0 6px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${label}</div>`,
    iconSize: [width, 30],
    iconAnchor: [width / 2, 15],
  });
}

// "L-00006" -> "6", falls back to the raw value if no digits are found.
function leadNoLabel(leadNo) {
  const m = String(leadNo || "").match(/(\d+)/);
  if (!m) return leadNo || "?";
  return String(parseInt(m[1], 10));
}

export default function MapView() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  // Independent group toggles — Open is on by default (matches "less clutter" goal).
  const [showOpen, setShowOpen] = useState(true);
  const [showWon, setShowWon] = useState(false);
  const [showLost, setShowLost] = useState(false);

  useEffect(() => {
    api.get("/leads?light=1").then((r) => setLeads(r.data)).finally(() => setLoading(false));
  }, []);

  const withGPS = useMemo(() => leads.filter((l) => l.latitude && l.longitude), [leads]);

  // Count leads per group (only those with GPS matter for the map).
  const groupCounts = useMemo(() => {
    const c = { open: 0, won: 0, lost: 0 };
    for (const l of withGPS) {
      const s = (l.status || "NEW").toUpperCase();
      if (s === "WON") c.won++;
      else if (s === "LOST") c.lost++;
      else c.open++;
    }
    return c;
  }, [withGPS]);

  const visible = useMemo(() => withGPS.filter((l) => {
    const s = (l.status || "NEW").toUpperCase();
    if (s === "WON") return showWon;
    if (s === "LOST") return showLost;
    return showOpen;
  }), [withGPS, showOpen, showWon, showLost]);

  const center = useMemo(() => {
    if (!visible.length) return [20.5937, 78.9629];
    const lat = visible.reduce((a, b) => a + b.latitude, 0) / visible.length;
    const lng = visible.reduce((a, b) => a + b.longitude, 0) / visible.length;
    return [lat, lng];
  }, [visible]);

  return (
    <Shell>
      <div className="px-4 sm:px-8 py-5 sm:py-7">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>Map</h1>
            <p className="text-sm text-slate-500 mt-1" data-testid="map-summary">
              Showing {visible.length} of {withGPS.length} GPS-tagged lead{withGPS.length === 1 ? "" : "s"}
              {withGPS.length < leads.length ? ` · ${leads.length - withGPS.length} lead${leads.length - withGPS.length === 1 ? " has" : "s have"} no GPS` : ""}
            </p>
          </div>
          {/* Group toggles — Open is on by default so the map isn't cluttered with closed deals. */}
          <div className="flex flex-wrap gap-1.5" data-testid="map-group-filters">
            <FilterChip
              active={showOpen}
              onClick={() => setShowOpen((v) => !v)}
              count={groupCounts.open}
              label="Open"
              activeCls="bg-sky-600 text-white border-sky-600"
              badgeActiveCls="bg-white/25 text-white"
              testid="map-filter-open"
            />
            <FilterChip
              active={showWon}
              onClick={() => setShowWon((v) => !v)}
              count={groupCounts.won}
              label="Won"
              activeCls="bg-emerald-600 text-white border-emerald-600"
              badgeActiveCls="bg-white/25 text-white"
              testid="map-filter-won"
            />
            <FilterChip
              active={showLost}
              onClick={() => setShowLost((v) => !v)}
              count={groupCounts.lost}
              label="Lost"
              activeCls="bg-slate-600 text-white border-slate-600"
              badgeActiveCls="bg-white/25 text-white"
              testid="map-filter-lost"
            />
          </div>
        </div>
        <div className="sohom-card overflow-hidden" style={{ height: "calc(100vh - 220px)" }} data-testid="leaflet-map">
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-400">Loading map…</div>
          ) : (
            <MapContainer center={center} zoom={visible.length ? 11 : 5} style={{ height: "100%", width: "100%" }}>
              <LayersControl position="topright">
                <LayersControl.BaseLayer checked name="Street">
                  <TileLayer
                    attribution='&copy; OpenStreetMap'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                </LayersControl.BaseLayer>
                <LayersControl.BaseLayer name="Satellite">
                  <TileLayer
                    attribution='Tiles &copy; Esri'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />
                </LayersControl.BaseLayer>
              </LayersControl>
              {visible.map((l) => (
                <Marker key={l.id} position={[l.latitude, l.longitude]} icon={makeIcon(leadNoLabel(l.lead_no), l.status)}>
                  <Popup>
                    <div style={{ minWidth: 200 }}>
                      <div className="font-mono text-xs text-slate-500">{l.lead_no}</div>
                      <div className="font-bold text-base text-slate-900">{l.building_name || "Unnamed"}</div>
                      <div className="text-xs text-slate-600 mb-2">{l.locality || ""}{l.city ? ", " + l.city : ""}</div>
                      <span className={statusClass(l.status)}>{(l.status || "NEW").replace("_", " ")}</span>
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => navigate(`/leads/${l.id}`)} className="text-xs font-semibold text-sky-700 hover:underline">
                          VIEW DETAILS →
                        </button>
                        <a href={`https://www.google.com/maps?q=${l.latitude},${l.longitude}`} target="_blank" rel="noreferrer"
                           className="text-xs font-semibold text-sky-700 hover:underline inline-flex items-center gap-1">
                          GOOGLE MAPS <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}
        </div>
      </div>
    </Shell>
  );
}

function FilterChip({ active, onClick, count, label, activeCls, badgeActiveCls, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors inline-flex items-center gap-2 ${
        active ? activeCls : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
      }`}
    >
      {label}
      <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${active ? badgeActiveCls : "bg-slate-100 text-slate-500"}`}>{count}</span>
    </button>
  );
}
